// src/components/VisionAssistClockControls.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * VisionAssistClockControls
 *
 * PURE UI controls cluster for the Vision Assist session header.
 *
 * Current product contract:
 * - Compact header-only presentation: Status + Time left + Pause/Resume.
 * - Pause/Resume remains available because Vision Assist is minute-based.
 * - No Assistance Mode UI belongs here.
 * - No End/Finalize control is rendered.
 * - The clock is read-only and reaching 00:00 does NOT finalize the session.
 * - Parent owns all backend/session actions; this component only requests
 *   Pause/Resume through onTogglePause.
 *
 * Visual contract:
 * - Match the current AskLawly dashboard/header control family.
 * - Same compact 36px pill/button height used by the Vision Assist Hide control.
 * - Navy/slate palette, white/soft surfaces, light borders, restrained shadows.
 * - Do not consume a second row of vertical space unless the parent itself wraps.
 */
export default function VisionAssistClockControls(props) {
  const {
    styles = {},

    // session flags
    status = "unknown",
    isPaused = false,
    isCompleted = false,
    hardLocked = false,
    sessionId = "",

    // time left (authoritative from parent snapshot.remaining_seconds)
    remainingSeconds = null,

    // actions
    onTogglePause, // async (payload?) => void

    // busy flags (parent-managed)
    pauseBusy = false,
  } = props;

  // ---- Robust time-left resolution (supports legacy/current prop variants) ----
  const toFiniteNumberOrNull = (v) => {
    if (v === 0) return 0;
    if (v === null || v === undefined) return null;
    if (typeof v === "number" && Number.isFinite(v)) return v;

    if (typeof v === "string") {
      const trimmed = v.trim();
      if (!trimmed) return null;

      // Support MM:SS / HH:MM:SS if this component is ever reused directly.
      if (trimmed.includes(":")) {
        const parts = trimmed.split(":").map((part) => Number(part));
        if (parts.length && parts.every((part) => Number.isFinite(part))) {
          let seconds = 0;
          for (const part of parts) seconds = seconds * 60 + part;
          return Number.isFinite(seconds) ? seconds : null;
        }
      }

      const match = trimmed.replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
      if (!match) return null;

      const n = Number(match[0]);
      return Number.isFinite(n) ? n : null;
    }

    return null;
  };

  const resolvedRemainingSeconds =
    toFiniteNumberOrNull(remainingSeconds) ??
    toFiniteNumberOrNull(props.remaining_seconds) ??
    toFiniteNumberOrNull(props.remainingSeconds) ??
    toFiniteNumberOrNull(props.timeLeftSeconds) ??
    toFiniteNumberOrNull(props.time_left_seconds) ??
    (() => {
      const ms =
        toFiniteNumberOrNull(props.remainingMs) ??
        toFiniteNumberOrNull(props.remaining_ms) ??
        toFiniteNumberOrNull(props.timeLeftMs) ??
        toFiniteNumberOrNull(props.time_left_ms);

      return typeof ms === "number" ? Math.floor(ms / 1000) : null;
    })();

  const formatMMSS = (totalSeconds) => {
    if (typeof totalSeconds !== "number" || !Number.isFinite(totalSeconds)) return "—";
    const s = Math.max(0, Math.floor(totalSeconds));
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const normalizedStatus = String(status || "unknown").trim().toLowerCase();

  // ----- optimistic pause UI -----
  const [localPaused, setLocalPaused] = useState(!!isPaused);
  const pauseOptimisticRef = useRef(false);

  useEffect(() => {
    if (!pauseOptimisticRef.current) {
      setLocalPaused(!!isPaused);
    }
  }, [isPaused]);

  const displayStatus = localPaused ? "paused" : normalizedStatus;

  const displayStatusLabel = useMemo(() => {
    const value = String(displayStatus || "").trim().toLowerCase();

    if (!value || value === "unknown") return "Connecting";
    if (value === "idle") return "Idle";
    if (value === "active") return "Active";
    if (value === "paused") return "Paused";
    if (value === "thinking") return "Thinking";
    if (value === "analyzing") return "Analyzing";
    if (value === "working" || value === "processing") return "Working";
    if (value === "minutes_unavailable" || value === "minutes unavailable") {
      return "Minutes unavailable";
    }

    return value
      .replace(/[_-]+/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase());
  }, [displayStatus]);

  const statusDotColor = useMemo(() => {
    const value = String(displayStatus || "").trim().toLowerCase();

    if (localPaused || value === "paused") return "#F59E0B";
    if (value === "thinking" || value === "analyzing" || value === "working" || value === "processing") {
      return "#2563EB";
    }
    if (
      value === "active" ||
      value === "idle"
    ) {
      return "#16A34A";
    }
    if (
      value === "minutes_unavailable" ||
      value === "minutes unavailable" ||
      value === "completed" ||
      value === "ended" ||
      value === "closed"
    ) {
      return "#94A3B8";
    }

    return "#94A3B8";
  }, [displayStatus, localPaused]);

  // A zero clock value does not create any lock in this component.
  // Existing external completed/locked state is still respected.
  const disabledCommon = !sessionId || isCompleted || hardLocked;

  const handleTogglePause = async () => {
    if (disabledCommon || pauseBusy) return;
    if (typeof onTogglePause !== "function") return;

    const nextPaused = !localPaused;
    const nextStatus = nextPaused ? "paused" : "active";

    pauseOptimisticRef.current = true;
    setLocalPaused(nextPaused);

    try {
      await onTogglePause({
        sessionId,
        nextPaused,
        nextStatus,
        reason: nextPaused ? "user_clicked_pause" : "user_clicked_resume",
      });
    } catch (e) {
      console.error("[VisionAssistClockControls] onTogglePause error:", e);
      setLocalPaused((prev) => !prev);
    } finally {
      pauseOptimisticRef.current = false;
    }
  };

  // ---------------------------------------------------------------------------
  // Compact AskLawly header styling.
  //
  // We intentionally prefer the parent's current header styles when available,
  // especially headerToggleBtn. This keeps Pause/Resume visually aligned with
  // the sibling Hide/Show control rendered by VisionAssistSession.
  // ---------------------------------------------------------------------------
  const clusterStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
    ...(styles.modeRightGrid || {}),
  };

  const statusBadgeStyle = {
    minHeight: 36,
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "0 11px",
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#334155",
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(styles.statusBadgeInline || {}),
  };

  const statusDotStyle = {
    width: 8,
    height: 8,
    flex: "0 0 auto",
    borderRadius: 999,
    background: statusDotColor,
    boxShadow: `0 0 0 3px ${statusDotColor}18`,
  };

  const clockPillStyle = {
    minHeight: 36,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    padding: "0 11px",
    borderRadius: 999,
    border: "1px solid #CBD5E1",
    background: "#F8FAFC",
    color: "#0F172A",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(styles.clockPill || {}),

    // Lock this control to the same compact one-line header family even if
    // an older parent style still contains vertical clock presentation.
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 36,
    padding: "0 11px",
    borderRadius: 999,
  };

  const clockLabelStyle = {
    fontSize: 10,
    color: "#64748B",
    fontWeight: 900,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    lineHeight: 1,
    ...(styles.clockLabel || {}),

    // Keep compact even when old parent styles are passed.
    margin: 0,
    fontSize: 10,
    lineHeight: 1,
  };

  const clockValueStyle = {
    fontSize: 13,
    lineHeight: 1,
    fontWeight: 950,
    color: "#0F172A",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.01em",
    ...(styles.clockValue || {}),

    // Remove the old vertical spacing.
    marginTop: 0,
    fontSize: 13,
    lineHeight: 1,
  };

  const pauseButtonBase =
    styles.headerToggleBtn || {
      minHeight: 36,
      padding: "0 12px",
      borderRadius: 999,
      border: "1px solid #CBD5E1",
      background: "#FFFFFF",
      color: "#1E2A78",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 900,
    };

  const pauseButtonStyle = {
    ...pauseButtonBase,
    minHeight: 36,
    padding: "0 12px",
    borderRadius: 999,
    border: localPaused ? "1px solid #A5B4FC" : "1px solid #CBD5E1",
    background: localPaused ? "#EEF2FF" : "#FFFFFF",
    color: "#1E2A78",
    fontSize: 12,
    fontWeight: 900,
    lineHeight: 1,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    cursor: disabledCommon || pauseBusy ? "not-allowed" : "pointer",
    opacity: disabledCommon || pauseBusy ? 0.58 : 1,
    transition:
      "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease",
  };

  return (
    <div style={clusterStyle} aria-label="Vision Assist session controls">
      <div style={statusBadgeStyle} title={`Status: ${displayStatusLabel}`}>
        <span style={statusDotStyle} aria-hidden="true" />
        <span>{displayStatusLabel}</span>
      </div>

      {/* Read-only usage clock. 00:00 does not finalize/close anything here. */}
      <div style={clockPillStyle} title="Vision Assist time remaining">
        <span style={clockLabelStyle}>Time left</span>
        <span style={clockValueStyle}>{formatMMSS(resolvedRemainingSeconds)}</span>
      </div>

      {/* Pause/Resume is intentionally the same control family as Header Hide/Show. */}
      <button
        type="button"
        style={pauseButtonStyle}
        onClick={handleTogglePause}
        disabled={disabledCommon || pauseBusy}
        title={localPaused ? "Resume Vision Assist" : "Pause Vision Assist"}
      >
        {pauseBusy ? "Working…" : localPaused ? "Resume" : "Pause"}
      </button>

      {/*
        END / FINALIZE INTENTIONALLY NOT RENDERED.

        The parent/backend compatibility implementation may remain dormant for
        future product changes, but this current control cluster exposes only:
        Status + Time left + Pause/Resume.
      */}
    </div>
  );
}

VisionAssistClockControls.displayName = "VisionAssistClockControls";
